import { lazy, Suspense, useEffect, memo, useMemo } from "react";
import { useMonaco } from "@monaco-editor/react";
import { Spinner } from "./spinner";

// Lazy load the Monaco Editor to reduce initial bundle size
const Editor = lazy(() =>
  import("@monaco-editor/react").then((module) => ({
    default: module.default,
  })),
);

interface CodeViewerProps {
  value: string;
  language?: string;
  height?: string;
  fontFamily?: string;
}

// Loading fallback component for Monaco Editor
const EditorSkeleton = memo(({ height }: { height: string }) => (
  <div
    className="bg-dark-surface flex items-center justify-center text-white"
    style={{ height }}
  >
    <div className="text-center">
      <Spinner size="lg" className="stroke-white!" />
    </div>
  </div>
));

EditorSkeleton.displayName = "EditorSkeleton";

function CodeViewer({
  value,
  language = "json",
  height = "100%",
  fontFamily = "'DM Mono', 'Courier New', monospace",
}: CodeViewerProps) {
  const monaco = useMonaco();

  // Memoize the theme definition to prevent recreation on every render
  const customTheme = useMemo(
    () => ({
      base: "vs" as const,
      inherit: true,
      rules: [
        { token: "", foreground: "#FFFFFF" },
        { token: "string.key.json", foreground: "#FFFFFF" },
        { token: "string.value.json", foreground: "#FFFFFF" },
        { token: "number", foreground: "#FFFFFF" },
        { token: "keyword", foreground: "#FFFFFF" },
        { token: "delimiter", foreground: "#FFFFFF" },
        { token: "delimiter.bracket", foreground: "#FFFFFF" },
        { token: "delimiter.array", foreground: "#FFFFFF" },
        { token: "delimiter.object", foreground: "#FFFFFF" },
      ],
      colors: {
        "editor.background": "#1B1B1B",
        "editorGutter.background": "#000000",

        // Follow Link popup (Quick Fix / Clickable links)
        "editorLink.activeForeground": "#F97316",
        "editorHoverWidget.background": "#FFF7ED",
        "editorHoverWidget.foreground": "#2B241F",
        "editorHoverWidget.border": "#2B241F",

        // Suggest widget
        "editorSuggestWidget.background": "#2d2d2d",
        "editorSuggestWidget.foreground": "#ffffff",
        "editorSuggestWidget.selectedBackground": "#F9731666",
        "editorSuggestWidget.highlightForeground": "#F97316",

        // Keep numbers white
        "editorLineNumber.foreground": "#FFFFFF",
        "editorLineNumber.activeForeground": "#FFFFFF",

        // Cursor, selection, highlight
        "editorCursor.foreground": "#F97316",
        "editor.selectionBackground": "#F9731655",
        "editor.lineHighlightBackground": "#00000000",

        // Vertical indent lines with low opacity
        "editorIndentGuide.background": "#FFFFFF22",
        "editorIndentGuide.activeBackground": "#FFFFFF44",

        // Scrollbars
        "scrollbarSlider.background": "#d6cfc4cc",
        "scrollbarSlider.hoverBackground": "#d6cfc4",
        "scrollbarSlider.activeBackground": "#d6cfc4",
      },
    }),
    [],
  );

  // Memoize editor options to prevent recreation on every render
  const editorOptions = useMemo(
    () => ({
      readOnly: true,
      lineNumbers: "on" as const,
      minimap: { enabled: false },
      stickyScroll: { enabled: false },
      wordWrap: "on" as const,
      folding: false,
      lineNumbersMinChars: 6,
      scrollBeyondLastLine: false,
      fontSize: 12,
      fontFamily: fontFamily,
      smoothScrolling: false,
      cursorSmoothCaretAnimation: "off" as const,
      mouseWheelZoom: true,
      renderWhitespace: "none" as const,
      links: true,
      fixedOverflowWidgets: true,
      scrollbar: {
        vertical: "visible" as const,
        horizontal: "visible" as const,
        verticalScrollbarSize: 6,
        horizontalScrollbarSize: 6,
        alwaysConsumeMouseWheel: false,
      },
    }),
    [fontFamily],
  );

  useEffect(() => {
    if (monaco) {
      monaco.editor.defineTheme("customJsonTheme", customTheme);
      monaco.editor.setTheme("customJsonTheme");
    }
  }, [monaco, customTheme]);

  return (
    <Suspense fallback={<EditorSkeleton height={height} />}>
      <Editor
        height={height}
        defaultLanguage={language}
        value={value}
        theme="customJsonTheme"
        options={editorOptions}
      />
    </Suspense>
  );
}

export default memo(CodeViewer);
